import { Line, LineChart, ResponsiveContainer } from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getCoverageColor,
  getGradeColor,
  getSectorColor,
  getValidationColor,
  PILLAR_CONFIG,
} from '@/lib/constants';
import { FileText, GitCompareArrows, Minus, ShieldCheck, TrendingDown, TrendingUp } from 'lucide-react';

export default function CompanyCard({ company, rank, onClick, isComparing, onCompareToggle }) {
  const gradeColor = getGradeColor(company.grade);
  const sectorColor = getSectorColor(company.sector);
  const validationColor = getValidationColor(company.validation_status);
  const coverageColor = getCoverageColor(company.coverage_level);

  const trajectoryIcon = company.improvement_trajectory === 'Improving'
    ? <TrendingUp className="h-3.5 w-3.5 text-[#2A9D8F]" />
    : company.improvement_trajectory === 'Declining'
      ? <TrendingDown className="h-3.5 w-3.5 text-[#FF6B6B]" />
      : <Minus className="h-3.5 w-3.5 text-white/35" />;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onClick();
      }}
      data-testid="scorecard-company-card"
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.92)] p-4 shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-colors duration-150 hover:border-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(78,205,196,0.55)]"
      style={{ borderLeftWidth: '3px', borderLeftColor: gradeColor }}
    >
      <div className="absolute inset-x-0 top-0 h-px bg-white/10" />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 font-mono text-[11px] font-semibold text-white/70 tabular-nums">
              {rank}
            </span>
            <Badge className="border border-white/10 bg-white/[0.05] text-[10px] uppercase tracking-[0.14em] text-white/60">
              {company.gp_country}
            </Badge>
          </div>
          <h3 className="truncate text-base font-semibold text-white" data-testid="scorecard-company-open-modal-button">
            {company.name}
          </h3>
          <p className="mt-1 text-xs text-white/40">
            HQ {company.hq_country} &middot; {company.sub_sector}
          </p>
        </div>

        <Badge variant="outline" className="border-white/10 text-[11px]" style={{ color: sectorColor, borderColor: `${sectorColor}40` }}>
          {company.sector}
        </Badge>
      </div>

      <div className="mt-4 flex items-end gap-3">
        <div>
          <p className="font-mono text-4xl font-bold leading-none text-white tabular-nums" data-testid="company-score">
            {company.overall_score}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge className="border border-white/10 text-xs font-bold" style={{ backgroundColor: `${gradeColor}24`, color: gradeColor }}>
              {company.grade}
            </Badge>
            <Badge className="border border-white/10 text-xs font-medium" style={{ backgroundColor: `${coverageColor}20`, color: coverageColor }}>
              {company.coverage_level}
            </Badge>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1 rounded-full bg-white/[0.04] px-2.5 py-1">
          {trajectoryIcon}
          <span className={`font-mono text-xs tabular-nums ${company.improvement_pct >= 0 ? 'text-[#4ECDC4]' : 'text-[#FF6B6B]'}`}>
            {company.improvement_pct > 0 ? '+' : ''}{company.improvement_pct}%
          </span>
        </div>
      </div>

      <div className="mt-4 space-y-2.5">
        {PILLAR_CONFIG.map((pillar) => (
          <div key={pillar.key} className="flex items-center gap-2">
            <span className="w-16 text-[10px] uppercase tracking-[0.12em] text-white/35">{pillar.name.split(' ')[0]}</span>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
              <div className="h-full rounded-full" style={{ width: `${company[pillar.key]}%`, backgroundColor: pillar.color }} />
            </div>
            <span className="w-8 text-right font-mono text-[11px] text-white/65 tabular-nums">{company[pillar.key]}</span>
          </div>
        ))}
      </div>

      {company.historical_scores?.length ? (
        <div className="mt-4 h-10 rounded-xl border border-white/5 bg-white/[0.03] px-1 py-1">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={company.historical_scores}>
              <Line type="monotone" dataKey="overall_score" stroke="rgba(78,205,196,0.7)" strokeWidth={1.8} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-white/5 pt-3">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 text-xs text-white/45">
                <ShieldCheck className="h-3.5 w-3.5" style={{ color: validationColor }} />
                <span className="capitalize">{company.validation_status.replace('_', ' ')}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="border-white/10 bg-[#0D2238] text-xs text-white/80">
              <p>{company.coverage_note}</p>
              <p className="mt-1">Verification: {company.verification_status}</p>
              <p>Source: {company.data_source}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 text-xs text-white/40">
                <FileText className="h-3.5 w-3.5 text-[#3498DB]" />
                <span>{company.third_party_refs?.length || 0} refs</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="border-white/10 bg-[#0D2238] text-xs text-white/80">
              <p>{company.validation_notes}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={(event) => {
            event.stopPropagation();
            onCompareToggle();
          }}
          data-testid="scorecard-compare-toggle"
          className={`rounded-full border px-3 text-xs ${
            isComparing
              ? 'border-[#2A9D8F]/40 bg-[#2A9D8F]/15 text-[#D8FBF7]'
              : 'border-white/10 bg-white/[0.03] text-white/55 hover:bg-white/[0.08]'
          }`}
        >
          <GitCompareArrows className="mr-1 h-3.5 w-3.5" />
          {isComparing ? 'Selected' : 'Compare'}
        </Button>
      </div>
    </div>
  );
}
